import axios, { AxiosError, AxiosHeaders } from "axios";
import { jwtDecode } from "jwt-decode";
import { AuthResponse, ApiSuccess, TokenPair } from "../types/apiTypes";
import { ResponsibleUserService } from "./ResponsibleUserService";
import { ScenarioService } from "./ScenarioService";
import { TimeUtil } from "../utils/TimeUtil";
import PKCEUtils from "../utils/PKCEUtils";

const RESPONSIBLE_USER_HEADER = "X-Responsible-User-Id";
const SCENARIO_ID_HEADER = "X-Scenario-Id";

export const API_PATHS = {
  getCalendarDates: "/retrieve-planning-dates",
  initiateNexusPlan: "/start-nexus-data-extraction",
  getNexusFilters: "/retrieve-nexus-filters",
  getActiveNexusFilters: "/retrieve-active-nexus-filters",
  getNexusPlan: "/gantt-chart-data",
  getCitizenKPIList: "/todo:addhere",
  retrieveKPIs: "/kpi-dashboard-data",
  retrievePondooStatus: "/jobs/state",
  startOptimization: "/start-optimization",
  stopOptimization: "/stop-optimization",
  postPreferrence: "/employee-favorability",
  nexusLanding: "/nexus-landing",
  createOrUpdateNexusFilter: "/get-or-update-nexus-filter",
  postNexusPlan: "/send-plan-to-nexus",
  activeJobHistories: "/active-job-histories",
  websocketJobs: "/job-message-history",
  getLatestOptimizationJobs: "/get-optimization-jobs",
  postLockShiftSchedule: "/lock-shift-schedule",
  lockTask: "/lock-task",
  getFullPlanViewState: "/get-full-plan-state", // DEPRECATED
  getFullPlanViewStateV2: "/get-full-plan-state-v2",
  errorLogging: "/log-error",
  dragTelemetry: "/drag-and-drop/client-telemetry",
  undoChange: "/undo",
  getCalendarSettings: "/calendar-settings",
  updateCalendarTimeWindow: "/calendar-settings/edit-calendar-time-window",
  updateEmployeeWorkTime: "/shift-settings/edit-shift-work-times",
  editTransportSegments: "/shift-settings/edit-transport-segments",
  deleteEmployeeWorkTime: "/shift-settings/clear-shift-work-times",
  clearTransportSegments: "/shift-settings/clear-transport-segments",
  getEmployeePreferencesByCitizen: "/shift-preferences-by-citizen",
  lockedRoutesStatus: "/locked-routes-status",
  lockAllRoutes: "/lock-all-routes",
  unlockAllRoutes: "/unlock-all-routes",
  getRulesets: "/rulesets",
  getContingencyPresets: "/contingency-presets",
  getConstraintCategories: "/constraint-categories",
  getConcernCategories: "/get-concern-categories",
  addConcernCategory: "/add-concern-category",
  editConcernCategory: "/edit-concern-category",
  deleteConcernCategory: "/delete-concern-category",
  getConcernCategoryImpact: "/get-concern-category-impact",
  getEmployeeConstraints: "/get-employee-constraints",
  getClientConstraints: "/get-service-client-constraints",
  addEmployeeConstraint: "/add-employee-constraint",
  addClientConstraint: "/add-service-client-constraint",
  editEmployeeConstraint: "/edit-employee-constraint",
  deleteEmployeeConstraint: "/delete-employee-constraint",
  deleteClientConstraint: "/delete-service-client-constraint",
} as const;

const api = axios.create({
  baseURL: window._env_.REACT_APP_API_URL,
});

// Callback registered by the response interceptor to notify the app that a new
// token has been set (see incrementTokenSetCount in apiConfigurationState).
let signalTokenSetCallback: (() => void) | null = null;

// A single in-flight refresh promise shared across the entire app (the Axios
// interceptor, the WebSocket heartbeat/reconnect, and app boot). It guarantees
// the rotating refresh token is only ever exchanged once at a time: concurrent
// callers await the same promise instead of each POSTing the now-stale refresh
// token. The latter is what triggered the spurious redirects to login.
let refreshPromise: Promise<TokenPair | null> | null = null;

// True if the access-token JWT is still valid for at least `bufferSeconds`.
// Used to detect that another browser tab already refreshed the token while we
// waited on the cross-tab lock, so we can reuse it instead of spending (and
// rotating away) the refresh token a second time.
//
// IMPORTANT: this buffer must be LARGER than the WebSocket heartbeat's proactive
// refresh lead time (60s, see isTokenExpired in WebSocketProvider). Otherwise a
// proactive refresh fired ~60s before expiry would see "still >Xs left, reuse"
// and skip — deferring the real refresh until AFTER the token has expired. A
// just-refreshed token has the full ~5 min, so 90s cleanly distinguishes
// "another tab refreshed it" from "about to expire, needs refreshing".
const isAccessTokenFresh = (token: string, bufferSeconds = 90): boolean => {
  try {
    const { exp } = jwtDecode<{ exp: number }>(token);
    return exp * 1000 > Date.now() + bufferSeconds * 1000;
  } catch {
    return false;
  }
};

// Runs `task` while holding an origin-wide exclusive lock, so only one browser
// tab refreshes the rotating refresh token at a time. Without this, two tabs
// hitting 401 together both POST the same refresh token; the IdP rotates it on
// the first call and rejects the second with a 400, logging that tab out. Falls
// back to running unlocked where the Web Locks API is unavailable.
const withRefreshLock = <T>(task: () => Promise<T>): Promise<T> => {
  if (typeof navigator !== "undefined" && navigator.locks?.request) {
    return navigator.locks.request("qamfsm-token-refresh", task);
  }
  return task();
};

/**
 * Refresh the access token using the stored refresh token. Concurrent calls are
 * de-duplicated within this tab (shared in-flight promise) and serialized across
 * tabs (Web Locks), so the rotating refresh token is never used twice in
 * parallel.
 * @returns the new (or already-refreshed) token pair, or null if the refresh
 * did not succeed (a login redirect has been triggered for genuine auth
 * failures).
 */
export const refreshAccessToken = (): Promise<TokenPair | null> => {
  // De-duplicate concurrent refreshes within THIS tab.
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async (): Promise<TokenPair | null> => {
    try {
      return await withRefreshLock(async () => {
        // Another tab may have refreshed while we waited for the lock — if the
        // stored access token is now fresh, reuse it instead of rotating the
        // refresh token again (which would invalidate the token that tab holds).
        const storedAccess = localStorage.getItem("access_token");
        const storedRefresh = localStorage.getItem("refresh_token");
        if (storedAccess && storedRefresh && isAccessTokenFresh(storedAccess)) {
          return { accessToken: storedAccess, refreshToken: storedRefresh };
        }

        if (!storedRefresh) {
          console.error(
            "Attempted to refresh token but localStorage was empty",
          );
          AuthService.redirectToLogin();
          return null;
        }

        // refreshConcreteToken persists the new pair to localStorage and
        // redirects to login itself if the server rejects the refresh token.
        const tokens = await AuthService.refreshConcreteToken(storedRefresh);
        if (!tokens) return null;

        signalTokenSetCallback?.();
        return tokens;
      });
    } catch (error) {
      // Transient/network failure — don't clear the session, let the caller's
      // request fail and retry naturally on the next call.
      console.error("Token refresh request failed:", error);
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

export const setupAuthInterceptor = (bearerToken: string) => {
  // Clear existing interceptors if any
  api.interceptors.request.clear();

  api.interceptors.request.use((config) => {
    // Read the freshest token from storage so a refresh performed anywhere
    // (interceptor, WebSocket heartbeat, boot) is reflected on the very next
    // request without having to re-register this interceptor.
    const token = localStorage.getItem("access_token") ?? bearerToken;
    config.headers["Authorization"] = `Bearer ${token}`;

    const responsibleUserId =
      ResponsibleUserService.getInstance().getResponsibleUserId();
    if (responsibleUserId) {
      config.headers[RESPONSIBLE_USER_HEADER] = responsibleUserId;
    }

    // Attach the current scenario as a header so flight-aware endpoints can
    // resolve the document scope without each call site threading idScenario.
    // Updated by the gantt-chart-data action; see ScenarioService.
    const idScenario = ScenarioService.getInstance().getIdScenario();
    if (idScenario != null) {
      config.headers[SCENARIO_ID_HEADER] = String(idScenario);
    }

    return config;
  });
};

export const setupRefreshTokenInterceptor = (signalTokenSet: () => void) => {
  signalTokenSetCallback = signalTokenSet;
  api.interceptors.response.clear();
  api.interceptors.response.use(
    (response) => {
      const xResponsibleUserId = (response.headers as AxiosHeaders).get(
        RESPONSIBLE_USER_HEADER,
      );
      if (xResponsibleUserId) {
        ResponsibleUserService.getInstance().setResponsibleUserId(
          xResponsibleUserId as string,
        );
      }

      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        // All concurrent 401s await the same shared refresh, so the rotating
        // refresh token is exchanged exactly once.
        const tokens = await refreshAccessToken();
        if (!tokens) {
          // refreshAccessToken already triggered the login redirect when the
          // failure was a genuine auth rejection.
          return Promise.reject(error);
        }

        originalRequest.headers["Authorization"] =
          `Bearer ${tokens.accessToken}`;
        return api(originalRequest);
      }

      return Promise.reject(error);
    },
  );
};

export class AuthService {
  static tokenURL = window._env_.REACT_APP_TOKEN_URL;
  static loginURL = window._env_.REACT_APP_LOGIN_URI;
  static clientId = window._env_.REACT_APP_CLIENT_ID;
  static clientSecret = window._env_.REACT_APP_CLIENT_SECRET;

  static headers: Record<string, string> = {
    "Content-Type": "application/x-www-form-urlencoded",
  };

  static redirectToLogin = () => (window.location.href = this.loginURL);

  static refreshConcreteToken = async (
    refreshToken: string,
  ): Promise<TokenPair | undefined> => {
    const params = new URLSearchParams({
      grant_type: "refresh_token",
      refresh_token: refreshToken as string,
      client_id: this.clientId,
      client_secret: this.clientSecret,
    });

    // Call token refresh endpoint using fetch, to avoid triggering an infinite loop in the Axios intercept handler
    const response = await fetch(this.tokenURL, {
      method: "POST",
      headers: this.headers,
      body: params,
    });

    if (response.status === 200) {
      const data = await response.json();
      return this.setAuthResponseInLocalStorageAndReturnToken({
        success: true,
        data: data,
      });
    } else {
      // Surface *why* the token endpoint rejected the refresh. For OAuth this is
      // a JSON body like {"error":"invalid_grant","error_description":"..."} —
      // e.g. "Token is not active" / "Stale token" point at an expired/rotated/
      // reused refresh token, vs "invalid_client" pointing at client config.
      const errorBody = await response.text().catch(() => "<unreadable body>");
      console.error(
        `Token refresh rejected: ${response.status} ${response.statusText} — ${errorBody}`,
      );
      this.redirectToLogin();
    }
  };

  /**
   * Redirects the user to an appropriate auth challenge with Nexus, and ensures that filterId and date are present
   * in the redirect uri.
   * @param filterId The Nexus ID of the filter in question
   * @param date Localdate representation of the calendar date (e.g. 2025-02-10)
   * @param filterName The representational name in Nexus for this particular filter/calendar
   * @param resourceIds A string-based array of employees/routes to extract in the fetch query
   */
  static performLogin = async (
    filterId: string,
    date: string,
    filterName: string,
    resourceIds: string | null,
  ) => {
    let pkce = await PKCEUtils.generatePKCE();

    let code_verifier = pkce[0];
    let code_challenge = pkce[1];

    localStorage.setItem("code_verifier", code_verifier);

    const authUrl = new URL(window._env_.REACT_APP_AUTH_URL);

    const redirectUri = this.getNexusAuthReturnUrl();

    const queryPayload = {
      date,
      filterId,
      filterName,
      resourceIds,
    };
    const b64state = btoa(JSON.stringify(queryPayload));

    authUrl.searchParams.append("client_id", window._env_.REACT_APP_CLIENT_ID);
    authUrl.searchParams.append("code_challenge", code_challenge);
    authUrl.searchParams.append("code_challenge_method", "S256");
    authUrl.searchParams.append("response_type", "code");
    authUrl.searchParams.append("redirect_uri", redirectUri.toString());
    authUrl.searchParams.append("state", b64state);

    window.location.href = authUrl.toString();
  };

  public static exchangeCodeForToken = async (): Promise<
    TokenPair | undefined
  > => {
    // Get clean redirect URI without query parameters - must match the one used in performLogin
    const redirectUri = this.getNexusAuthReturnUrl().toString();

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    const codeVerifier = localStorage.getItem("code_verifier");

    if (!code || !codeVerifier) {
      return Promise.reject("Code or code verifier couldn't be found");
    }

    const body = new URLSearchParams({
      grant_type: "authorization_code",
      client_id: window._env_.REACT_APP_CLIENT_ID,
      client_secret: window._env_.REACT_APP_CLIENT_SECRET,
      code: code!,
      code_verifier: codeVerifier!,
      redirect_uri: redirectUri,
    });

    const headers: Record<string, string> = {
      "Content-Type": "application/x-www-form-urlencoded",
    };

    try {
      const authTokenResponse = await api.post<AuthResponse>(
        window._env_.REACT_APP_TOKEN_URL,
        body,
        headers,
      );

      if (authTokenResponse.status === 200) {
        return this.setAuthResponseInLocalStorageAndReturnToken({
          success: true,
          data: authTokenResponse.data,
        });
      }
    } catch (error) {
      console.error(error);
      const errorPayload =
        error instanceof AxiosError
          ? JSON.stringify(error.toJSON())
          : JSON.stringify({ message: (error as Error).message });
      await api.post(API_PATHS.errorLogging, errorPayload);
    }
  };

  private static getNexusAuthReturnUrl = (): URL =>
    new URL(
      `${window.location.protocol}//${window.location.host}/nexus-landing`,
    );

  private static setAuthResponseInLocalStorageAndReturnToken = (
    authResponse: ApiSuccess<AuthResponse>,
  ): TokenPair => {
    const { access_token, expires_in, refresh_token, refresh_expires_in } =
      authResponse.data;

    localStorage.setItem("last_set_time", TimeUtil.dateTimeNowString());
    localStorage.setItem("access_token", access_token);
    localStorage.setItem("access_token_expires_in", expires_in);
    localStorage.setItem("refresh_token", refresh_token);
    localStorage.setItem("refresh_expires_in", refresh_expires_in);

    return { accessToken: access_token, refreshToken: refresh_token };
  };
}

export default api;
